import { useState, useEffect, useCallback, useRef } from 'react';
// Phase B3: the access token lives in memory, not localStorage.
import { setAccessToken, clearAccessToken, getAccessToken, bootstrap, refusalEndsSession } from '../shared/api/tokenStore';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../api';
import { identityService } from '../shared/api';
import { APP_STORAGE_PREFIX } from '../config/config.js';
// THE window, the clock and the verdict all live in one place now — shared with tokenRefresh.js,
// which used to carry its own hard-coded copy of the same 26 minutes. See that file's header for
// the defaults, why they are what they are, and why the window is a per-tenant setting.
import {
  WARNING_MS,
  idleVerdict,
  endReason,
  lastActivityAt,
  markActivity,
  secondsUntilExpiry,
  SESSION_KEYS,
} from '../shared/api/sessionPolicy';

const CHECK_INTERVAL = 1000;               // Check every second
const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // Proactively refresh token every 10 min of activity

/**
 * Is there a live session?
 *
 * PRODUCTION INCIDENT 2026-07-30 — users logged out mid-work. Root cause: every effect below gated on
 * `getAccessToken()`, and under Phase B3 the access token lives in MEMORY ONLY, so it is *guaranteed
 * null on mount* — on first paint and after every reload there is no token until `bootstrap()` mints
 * one from the refresh cookie. So each `if (!token) return` bailed at mount. The activity-tracking
 * effect declared `[]` deps, so it never re-ran and its listeners NEVER ATTACHED: nothing in the app
 * ever updated `lastActivityRef`, the inactivity clock ran uninterrupted while the user was actively
 * working, and it logged them out. Clicking "Stay logged in" only reset the clock by hand, so the same
 * logout recurred 25 minutes later.
 *
 * `tokenStore.js`'s own header names this exact class of bug — "Auth state can no longer be 'is there
 * a token?' — on first paint there never is one" — and points at the durable signal: the persisted
 * user record, which survives reload and is removed on logout. That is what we gate on here, matching
 * `client.js`. Do not reintroduce a `getAccessToken()` guard at effect-setup time.
 */
function hasSession() {
  return !!localStorage.getItem(`${APP_STORAGE_PREFIX}_user`);
}

/** Everything this browser holds about the person who has just been signed out. */
function purgeSession() {
  clearAccessToken();
  localStorage.removeItem(`${APP_STORAGE_PREFIX}_access_token`);
  localStorage.removeItem(`${APP_STORAGE_PREFIX}_refresh_token`);
  localStorage.removeItem(`${APP_STORAGE_PREFIX}_user`);
  localStorage.removeItem(SESSION_KEYS.lastActivity);
  localStorage.removeItem(SESSION_KEYS.sessionStarted);
  // F-L4: purge auto-saved bid drafts (confidential pricing) on idle-timeout logout too.
  Object.keys(localStorage).filter(k => k.startsWith('bid_draft_')).forEach(k => localStorage.removeItem(k));
}

/**
 * Ask the SERVER to end the session, and leave this browser empty afterwards whatever happens.
 *
 * ── WHY AN IDLE SIGN-OUT HAS TO MAKE A NETWORK CALL AT ALL ───────────────────────────────────
 * The credential that grants access is the httpOnly `bfs_refresh` cookie — seven days, re-issued
 * with a fresh seven on every rotation — and JavaScript cannot delete it. So clearing localStorage
 * and navigating, which is all this used to do, signed somebody out of a URL and nothing else: the
 * cookie survived, `tokens_valid_after` was never stamped, and the next thing that touched an API
 * ran `bootstrap()`, minted a new token from that cookie and wrote the user record straight back.
 * That is the fifteen-hour session, and only the server can close it.
 *
 * ── WHY IT MINTS A TOKEN IN ORDER TO DESTROY ONE ─────────────────────────────────────────────
 * `/auth/logout` authenticates with the ACCESS token and clears the cookie as a side effect, and
 * after an idle period — or on any page load — there is no access token in memory. So this bootstraps
 * one from the cookie first. It reads oddly and it is the only route the endpoint offers; the
 * alternative is teaching /auth/logout to accept the refresh cookie as a credential, which is a
 * change to the most sensitive endpoint in the product and belongs in its own release.
 *
 * ── AND WHY IT PURGES AGAIN AT THE END ───────────────────────────────────────────────────────
 * `bootstrap()` writes `bfs_user` back on success. Leaving that behind would restore precisely the
 * state this function exists to end.
 */
// One at a time. `bootstrap()` writes `bfs_user` back mid-flight, and the staleness check re-runs on
// every navigation — including the one to /login this sign-out just made — so without this a second
// sign-out can start while the first is still in the air, and fire a second /auth/logout for the
// same session. It terminates either way (the purge closes it), but a burst of revocations for one
// timeout is noise in the audit log at best.
let endingSession = false;

async function endServerSession() {
  if (endingSession) return;
  endingSession = true;
  try {
    if (!getAccessToken()) await bootstrap();
    // No token means there was genuinely no session left to revoke — an expired or already-cleared
    // cookie. Nothing to ask the server for.
    if (getAccessToken()) await identityService.logout();
  } catch { /* offline, or already revoked. The purge below is unconditional for that reason. */ }
  finally {
    purgeSession();
    endingSession = false;
  }
}

/**
 * Manage the session timeout: warn, then sign out.
 *
 * The window is NOT a constant in this file any more. Both numbers — how long somebody may do
 * nothing, and how long a session may live however busy it is — are per-tenant settings resolved by
 * `shared/api/sessionPolicy.js`, which also holds the defaults, the bounds, and the reasoning for
 * all of it. Read that file before changing anything here.
 *
 * The two rules everything below obeys:
 *   1. THE PERSISTED STAMP IS THE TRUTH. `lastActivityRef` is a faster copy of it and nothing may
 *      advance the ref without advancing the stamp. Every mechanism that broke this clock broke
 *      that rule — a token rotation racing the ref forward, a "stay logged in" moving the ref and
 *      not the stamp.
 *   2. NOT KNOWING IS NOT FINE. An absent stamp means a session of unknown age, and that ends the
 *      session rather than starting a new window.
 */
export function useSessionTimeout() {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(WARNING_MS / 1000));
  // WHICH CLOCK is ending this session — 'idle' or 'absolute'. Wording only; the decision stays in
  // `idleVerdict`. It exists because "expired due to inactivity" is false on the absolute path, and
  // the person reading it has been working all day.
  const [endedBy, setEndedBy] = useState('idle');

  // Restore last-activity from localStorage so sleep/wake & tab-restore
  // don't silently reset the inactivity clock.
  //
  // NOTE THE MISSING FALLBACK. This used to end `: Date.now()`, which handed a session whose age
  // was unknown a complete fresh window — the single line that let a fifteen-hour-old session stay
  // signed in. A null now stays null all the way to `idleVerdict`, which reports it as 'unknown'
  // and ends the session. The ref is only ever a FASTER copy of the persisted stamp; the persisted
  // stamp is the truth, and nothing may advance the ref without advancing that too.
  const lastActivityRef = useRef(lastActivityAt());

  const warningTimerRef = useRef(null);
  const checkTimerRef = useRef(null);
  const proactiveRefreshTimerRef = useRef(null);
  const showWarningRef = useRef(false);
  // The REF as well as the state, for the same reason `showWarningRef` exists: `handleLogout` is a
  // useCallback that would otherwise close over a stale value and file the wrong reason.
  const endedByRef = useRef('idle');

  // Reset activity timestamp.
  //
  // PERSISTS, and that is the whole point of the change. This used to move only the in-memory ref,
  // leaving the stored stamp at its old value — so the every-navigation staleness check below read
  // a timestamp that said the user had been gone for half an hour, and threw out somebody who had
  // just told the app they were here. The ref and the stamp move together or not at all.
  const resetActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    markActivity(now);
    showWarningRef.current = false;
    setShowWarning(false);
    setSecondsRemaining(Math.ceil(WARNING_MS / 1000));

    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  // Record which clock is ending this session, before anything acts on it.
  const noteEndReason = useCallback(() => {
    const reason = endReason();
    endedByRef.current = reason;
    setEndedBy(reason);
    return reason;
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    // THE PERSON LEAVES NOW; the server is told in the background.
    //
    // Split deliberately. Somebody being signed out must not sit watching a spinner because the
    // network is slow, so the local purge and the navigation are synchronous. But a purely local
    // sign-out is not a sign-out at all — see `endServerSession` for why — so that call follows and
    // is allowed to take as long as it takes.
    purgeSession();
    localStorage.setItem(`${APP_STORAGE_PREFIX}_logout_event`, Date.now().toString()); // Signal other tabs
    // The REF, not just the state. Leaving `showWarningRef.current` true survived the logout, and
    // `handleActivity` returns early while it is set — so if a session was ever re-established in
    // this same page, the activity tracker could never record anything again for the life of the tab.
    showWarningRef.current = false;
    setShowWarning(false);
    navigate('/login', {
      replace: true,
      state: {
        message: endedByRef.current === 'absolute'
          // NOT "due to inactivity". This person was working; the session simply reached the
          // maximum length their company allows, which is a different thing and they can see it is.
          ? 'You have been signed out because this session reached the maximum length your company allows. Please log in again.'
          : 'Your session has expired due to inactivity. Please log in again.'
      }
    });

    endServerSession();
  }, [navigate]);

  // Silently refresh the token — used both proactively and when user clicks "Stay logged in"
  const silentRefresh = useCallback(async () => {
    try {
      const data = await authAPI.refreshToken();
      setAccessToken(data.access_token);
      // Phase B1: refresh token now lives only in the httpOnly cookie — don't persist it to localStorage.
      localStorage.removeItem(`${APP_STORAGE_PREFIX}_refresh_token`);
      if (data.user) {
        localStorage.setItem(`${APP_STORAGE_PREFIX}_user`, JSON.stringify(data.user));
      }
      localStorage.setItem(`${APP_STORAGE_PREFIX}_token_refresh`, Date.now().toString());
      return 'ok';
    } catch (error) {
      console.error('Token refresh failed:', error);
      // WHY it failed decides what "Stay logged in" does next: a refusal (401/403) is a verdict, a
      // 429 / restart / dropped connection is not (tokenStore.refusalEndsSession).
      return refusalEndsSession(error) ? 'refused' : 'busy';
    }
  }, []);

  // Handle stay logged in - refresh token and reset timer
  const handleStayLoggedIn = useCallback(async () => {
    // Immediately reset activity and hide warning to prevent race condition.
    // `resetActivity` also PERSISTS the stamp — pressing this button and then being thrown out on
    // the next screen anyway was a real defect, and it was this line not reaching localStorage.
    resetActivity();

    // One failed refresh is NOT proof the session is dead, and treating it as proof is what made the
    // "Stay logged in" button the most reliable way to get logged out. Refresh tokens rotate with
    // reuse detection (identity `auth.py` blacklists the presented jti on every rotation), and the
    // cookie is shared by every tab of the origin — so if another tab (or this tab's own proactive
    // timer, which the activity reset above re-arms) refreshed a moment ago, this call presents a
    // now-superseded jti and gets a 401 while the session is perfectly alive and the browser already
    // holds the rotated cookie. Retrying once picks up that new cookie and succeeds.
    if ((await silentRefresh()) === 'ok') return;
    await new Promise((resolve) => setTimeout(resolve, 400));
    const second = await silentRefresh();
    if (second === 'ok') return;
    // Pressing "Stay logged in" in a busy minute (429, identity restarting, a dropped connection) is
    // not a reason to sign the person out: they are demonstrably here, their activity is reset, and
    // the next request refreshes again. Only a second REFUSAL ends it. (Review of 21 Sep: two 429s
    // used to sign out the one person who had just pressed the button that says stay.)
    if (second === 'busy') return;
    handleLogout();
  }, [handleLogout, resetActivity, silentRefresh]);

  // On mount — and, because `handleLogout` follows `navigate`, on every in-app navigation — end the
  // session if the stored stamp is already past the window, before any API interceptor can silently
  // refresh the token.
  //
  // 'unknown' COUNTS. There used to be an `if (stored)` around this: with the key absent the effect
  // fell straight through and said nothing, and `lastActivityRef` had already defaulted to now. A
  // session whose age nobody could account for was therefore treated as brand new — and the key goes
  // absent on exactly the sessions that most need checking, because `handleLogout` deletes it and
  // `bootstrap()` restores `bfs_user` without it. Not knowing is a reason to ask for a password.
  useEffect(() => {
    if (!hasSession()) return;

    const verdict = idleVerdict();
    if (verdict === 'expired' || verdict === 'unknown') {
      if (verdict === 'expired') noteEndReason();
      handleLogout();
    }
  }, [handleLogout, noteEndReason]);

  // Check for inactivity
  useEffect(() => {
    // Only run if user is logged in
    if (!hasSession()) return;

    const checkActivity = () => {
      // Double-check the session still exists (in case user logged out). Gate on the persisted user,
      // not the in-memory access token: the token is briefly null during every rotation, and gating on
      // it here used to clearInterval() the timeout permanently on any refresh blip.
      if (!hasSession()) {
        // Token removed, stop checking
        if (checkTimerRef.current) {
          clearInterval(checkTimerRef.current);
          checkTimerRef.current = null;
        }
        setShowWarning(false);
        return;
      }
      
      // Judged from the PERSISTED stamp, via the shared verdict — never from the in-memory ref
      // alone. The ref could be raced ahead by things that were not a person (a token rotation in
      // another tab used to do exactly that, forever), and the stamp is what every other reader of
      // this policy — the staleness check above, tokenRefresh.js's silent-refresh brake — agrees on.
      // The absolute cap is inside `idleVerdict` too, so a session that has simply been alive too
      // long now ends here with the same two-minute warning an idle one gets, rather than being cut
      // off mid-sentence. It did not until 2026-09-08: the absolute branch returned 'expired' with
      // no warning stage at all, and the sign-out message then blamed inactivity.
      const verdict = idleVerdict();

      if (verdict === 'expired' || verdict === 'unknown') {
        if (verdict === 'expired') noteEndReason();
        handleLogout();
      } else if (verdict === 'warning') {
        // Show warning and update countdown
        if (!showWarningRef.current) {
          noteEndReason();
          showWarningRef.current = true;
          setShowWarning(true);
        }
        setSecondsRemaining(secondsUntilExpiry());
      } else {
        // Not yet time for warning
        if (showWarningRef.current) {
          showWarningRef.current = false;
          setShowWarning(false);
          setSecondsRemaining(Math.ceil(WARNING_MS / 1000));
        }
      }
    };

    // Check activity every second
    checkTimerRef.current = setInterval(checkActivity, CHECK_INTERVAL);

    return () => {
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
      }
    };
  }, [handleLogout, noteEndReason]); // removed showWarning dep — use showWarningRef to avoid stale closure

  // Track user activity
  useEffect(() => {
    // NOTE: no session gate at setup time and no early return here. This effect has `[]` deps, so it
    // runs exactly once; gating it on a value that is null at mount is what silently disabled activity
    // tracking for the whole session and caused the 2026-07-30 mid-work logouts. Attaching passive
    // listeners unconditionally is harmless — handleActivity checks the session itself.

    // Only track meaningful interactions (not passive mouse movement).
    //
    // WIDER THAN IT WAS, because the old four-event list was a much narrower signal than it read.
    // `wheel`, `click`, `pointerdown`, `input` and `change` were not tracked at all, so a person
    // driving a form with a mouse wheel and a datepicker could look idle. Still deliberately NOT
    // `mousemove` — a cat, a delivery van vibration or a jogging cursor is not somebody working.
    const activityEvents = ['mousedown', 'pointerdown', 'keydown', 'wheel', 'scroll', 'touchstart', 'input', 'change'];

    // Update activity timestamp immediately on any activity
    const handleActivity = () => {
      // Double-check the session still exists (in case user logged out)
      if (!hasSession()) return;

      // Don't track activity when warning is shown - user must make a choice
      if (showWarningRef.current) return;

      const now = Date.now();
      lastActivityRef.current = now;
      // Sync activity across tabs
      markActivity(now);
    };

    // CAPTURE PHASE, ON `document` — not the bubble phase on `window`, which is what this was.
    // `scroll` does not bubble from an element, and this app has ~89 inner `overflow-y-auto` panes
    // plus long tables inside fixed-height containers. Reading a forty-page field ticket in one of
    // them therefore produced ZERO activity events, and the person staring at the screen was
    // evicted for inactivity. Capture starts at the root and reaches the target, so it sees them.
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true, capture: true });
    });

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, { capture: true });
      });
    };
  }, []); // no deps — reads only refs, never stale

  // Handle browser sleep/wake: browsers throttle or completely pause setInterval
  // in background tabs. This listener fires immediately when the tab becomes
  // visible again and checks the real elapsed time.
  useEffect(() => {
    if (!hasSession()) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      if (!hasSession()) return;

      // Re-sync the ref from the stamp — ADOPTING IT WHICHEVER WAY IT MOVES.
      //
      // This used to adopt the stored time only `if (storedTime > lastActivityRef.current)`, i.e.
      // only ever forward. That looks like a safe merge and is the opposite: the ref was the value
      // a token rotation in another tab could race ahead, so under that loop the ref was ALWAYS
      // newer than the truth and this backstop — the mechanism written specifically to catch a tab
      // that had been asleep — could never fire. The stamp is the record of a person; the ref is a
      // cache of it. A cache does not overrule its source.
      lastActivityRef.current = lastActivityAt();

      const verdict = idleVerdict();

      if (verdict === 'expired' || verdict === 'unknown') {
        // Expired while asleep — log out immediately
        if (verdict === 'expired') noteEndReason();
        handleLogout();
      } else if (verdict === 'warning') {
        // Show warning
        if (!showWarningRef.current) {
          noteEndReason();
          showWarningRef.current = true;
          setShowWarning(true);
        }
        setSecondsRemaining(secondsUntilExpiry());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleLogout, noteEndReason]);

  // Proactively refresh the token every 10 minutes while the user is active
  useEffect(() => {
    if (!hasSession()) return;

    proactiveRefreshTimerRef.current = setInterval(async () => {
      if (!hasSession()) return;
      // Only refresh if the user has been active in the last 10 minutes — measured against the
      // persisted stamp, so this cannot be kept alive by anything that was not a person.
      const last = lastActivityAt();
      if (last !== null && Date.now() - last < TOKEN_REFRESH_INTERVAL) {
        await silentRefresh();
      }
    }, TOKEN_REFRESH_INTERVAL);

    return () => {
      if (proactiveRefreshTimerRef.current) {
        clearInterval(proactiveRefreshTimerRef.current);
      }
    };
  }, [silentRefresh]);

  // Cross-tab communication - listen for storage events
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Logout event - another tab logged out
      if (e.key === `${APP_STORAGE_PREFIX}_logout_event`) {
        setShowWarning(false);
        navigate('/login', { 
          replace: true,
          state: { message: 'You have been logged out.' }
        });
      }
      
      // A TOKEN REFRESH IN ANOTHER TAB IS NOT ACTIVITY, and this is where the timeout went.
      //
      // This block used to call `resetActivity()` on `bfs_token_refresh`. That key is written by
      // `silentRefresh`, which the 10-minute proactive timer fires whenever this tab's clock is
      // under 10 minutes old. So with two tabs open: tab A refreshes, tab B's handler resets B's
      // clock to now, B's own timer therefore refreshes 10 minutes later, which resets A's clock —
      // and round it goes, forever, with nobody at either keyboard. The inactivity clock in both
      // tabs never advanced past ten minutes and the 26-minute logout could not be reached.
      //
      // A rotation is the app talking to itself. It is not evidence a person is here, so it no
      // longer moves the clock. Real cross-tab activity still syncs, below, from the stamp that
      // only a person's input writes.

      // Activity sync - another tab had activity
      if (e.key === SESSION_KEYS.lastActivity) {
        const activityTime = parseInt(e.newValue, 10);
        if (!isNaN(activityTime)) {
          lastActivityRef.current = activityTime;
          // Hide warning if it was showing
          if (showWarning) {
            setShowWarning(false);
            setSecondsRemaining(Math.ceil(WARNING_MS / 1000));
          }
        }
      }

      // Session cleared in another tab - logout.
      // Phase B3: this used to watch the access-token key. That key no longer exists in
      // localStorage, so it would never fire again and cross-tab logout would silently break —
      // one tab logging out would leave every other tab live. The user record IS still persisted
      // and IS removed on logout, so it is the correct cross-tab signal now.
      if (e.key === `${APP_STORAGE_PREFIX}_user` && !e.newValue) {
        setShowWarning(false);
        navigate('/login', { 
          replace: true,
          state: { message: 'You have been logged out.' }
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, resetActivity, showWarning]);

  return {
    showWarning,
    secondsRemaining,
    // 'idle' or 'absolute'. The modal words itself from this, and on the absolute path it must not
    // offer to keep the person working — nothing it can do would extend that window.
    endedBy,
    handleStayLoggedIn,
    handleLogout,
    resetActivity
  };
}
